import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { currentUser } from 'auth-service-lib';
import { LocalFileNamespace } from 'datasource-service-lib';
import { IAttachmentMergeFile, IEntity } from 'interfaces-lib';
import { LocalfileUploadService } from 'localfile-upload-service';
import { MessageNotificationService } from 'message-notification-service-lib';
import { AutoGenIconPipe, TransformBytesToMB } from 'pipes-lib';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { ContentTypeMapperService, FileExtensionMatcherService } from 'utils-service-lib';
import { runtimeEnvironment } from '../../../../../environments/environment';
import { SimpleTableComponent } from '../../../../common-components/simple-table/simple-table.component';
import { ISimpleTableColumn, ISimpleTableRow } from '../../../../common-components/simple-table/simple-table.interfaces';
import { ILocalMergeFile } from '../../../../merge-component/merge-controller.service';
import { ServiceProviderService } from '../../../../services/service.provider.service';
import { AttachFileTableBaseComponent } from '../base/attach-file-table-base.component';
import { IFileInfo } from '../datasource/datasource-attach-file.component';

@Component({
  selector: 'app-local-attach-file',
  templateUrl: './local-attach-file.component.html',
  styleUrl: './local-attach-file.component.scss',
  standalone: false,
})
export class LocalAttachFileComponent extends AttachFileTableBaseComponent implements OnInit {
  private static sortByName(a: IFileInfo, b: IFileInfo): number {
    return a.name.localeCompare(b.name);
  }

  private static sortBySize(a: IFileInfo, b: IFileInfo): number {
    return (a.size ?? 0) - (b.size ?? 0);
  }

  private static sortByModified(a: IFileInfo, b: IFileInfo): number {
    return (a.dateModified ?? 0) - (b.dateModified ?? 0);
  }
  @Input() entityName: string;
  @ViewChild('fileInput') fileInputRef: ElementRef<HTMLInputElement>;
  @ViewChild('simpleTable') simpleTableRef: SimpleTableComponent;
  @Output() fileSelectedChange = new EventEmitter<IAttachmentMergeFile | undefined>();

  renderMode = runtimeEnvironment.renderMode;
  selectedFiles: ISimpleTableRow[] = [];
  fileSelected: IAttachmentMergeFile;

  columnHeadings: ISimpleTableColumn[] = [
    {
      title: 'Name',
      width: '50%',
      sortFunction: LocalAttachFileComponent.sortByName,
    },
    {
      title: 'File Size',
      width: '15%',
      sortFunction: LocalAttachFileComponent.sortBySize,
    },
    {
      title: 'Date Added',
      width: '30%',
      sortFunction: LocalAttachFileComponent.sortByModified,
    },
    { title: 'Actions', width: '5%' },
  ];

  constructor(
    messageNotificationService: MessageNotificationService,
    transformBytesToMB: TransformBytesToMB,
    datePipe: DatePipe,
    ngZone: NgZone,
    serviceProviderService: ServiceProviderService,
    sharedServiceProviderServiceFactory: SharedServiceProviderServiceFactory,
    identityStorage: IdentityStorage,
    fileExtensionMatcherService: FileExtensionMatcherService,
    contentTypeMapperService: ContentTypeMapperService,
    private localfileUploadService: LocalfileUploadService,
  ) {
    super(
      messageNotificationService,
      transformBytesToMB,
      datePipe,
      ngZone,
      serviceProviderService,
      sharedServiceProviderServiceFactory,
      identityStorage,
      fileExtensionMatcherService,
      contentTypeMapperService,
    );
  }
  protected onFilesLoaded(): void {
    // Not needed for `local-attach-file component`.
  }

  ngOnInit(): void {}

  triggerFilePicker(): void {
    this.fileInputRef.nativeElement.click();
  }

  clearFileSelection() {
    this.fileSelected = undefined;
    this.selectedFiles = [];
    this.simpleTableRef?.clearSelection();
    this.fileInputRef.nativeElement.value = '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      const source: IEntity = {
        id: currentUser.id,
        type: 'currentUser',
      };

      const localFile: ILocalMergeFile = {
        id: undefined,
        type: '',
        name: file.name,
        size: file.size,
        dateModified: file.lastModified,
        belongsTo: source,
        file: file,
      };

      this.localfileUploadService.uploadMergeFiles([localFile], runtimeEnvironment.origin.namespace).subscribe((value: any) => {
        const fileInfo: IAttachmentMergeFile = {
          id: localFile.id,
          type: localFile.type,
          name: localFile.name,
          size: localFile.size,
          dateModified: localFile.dateModified,
          belongsTo: localFile.belongsTo,
          namespace: LocalFileNamespace.name,
          path: value[0].path,
        };

        this.fileSelected = fileInfo;
        this.updateTable(fileInfo);
        this.fileSelectedChange.emit(fileInfo);
      });
    }
  }

  onActionClick(action: { item: IFileInfo; action: string }): void {
    if (action.action === 'remove-file') {
      const nameToRemove = action.item.name;
      this.selectedFiles = this.selectedFiles.filter((row) => row.data.name !== nameToRemove);
      this.fileSelected = undefined;
      this.fileSelectedChange.emit(undefined);
      this.fileInputRef.nativeElement.value = '';
    }
  }

  itemRowClick(file: IAttachmentMergeFile): void {
    this.fileSelectedChange.emit(file);
  }

  private updateTable(fileInfo: IAttachmentMergeFile) {
    this.selectedFiles = [
      {
        data: fileInfo,
        cells: [
          {
            text: fileInfo.name,
            autoIcon: AutoGenIconPipe.AUTO_GEN_ICON,
          },
          {
            text: fileInfo.size ? `${(fileInfo.size / 1024).toFixed(2)} KB` : '',
          },
          {
            text: fileInfo.dateModified ? new Date(fileInfo.dateModified).toLocaleString() : '',
          },
          {
            actions: [
              {
                icon: 'close',
                title: 'Remove',
                meta: 'remove-file',
                iconClasses: 'white-icon',
              },
            ],
          },
        ],
        selected: !!this.fileSelected,
      },
    ];
  }
}
